/**
 * @sazeo/sdk — CRM Lead Scoring hooks
 *
 * TanStack Query wrappers for lead scoring operations.
 * All calls route through the Edge Function at `/app/crm/scoring/*`
 * and enforce RLS via the caller's JWT.
 *
 * Paired spec: specs/CODEX_TASK_L92_LEAD_SCORING.md
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

export interface LeadScore {
  id: string;
  tenant_id: string;
  contact_id: string;
  deal_id?: string;
  contact_name?: string;
  company?: string;
  engagement_score: number;
  fit_score: number;
  behavior_score: number;
  ai_score: number;
  total_score: number;
  category: 'hot' | 'warm' | 'cold';
  ai_analysis?: string;
  ai_reasoning?: Record<string, any>;
  scored_at: string;
  scored_by_user?: string;
  expires_at: string;
}

export interface ScoreFactor {
  id: string;
  factor_name: string;
  factor_value: number;
  contribution: number;
  evidence?: Record<string, any>;
}

export interface ScoreHistoryPoint {
  score_at: string;
  score_value: number;
  category: 'hot' | 'warm' | 'cold';
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const scoringKeys = {
  all: ['crm-scoring'] as const,
  scores: (category?: string, page?: number) =>
    [...scoringKeys.all, 'scores', category ?? 'all', page ?? 1] as const,
  breakdown: (contactId: string) => [...scoringKeys.all, 'breakdown', contactId] as const,
  history: (contactId: string) => [...scoringKeys.all, 'history', contactId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/**
 * Fetch list of leads with scores, optionally filtered by category
 */
export function useLeadScores(
  supabase: SupabaseClientLike,
  filters?: { category?: 'hot' | 'warm' | 'cold'; search?: string; page?: number; size?: number },
) {
  const page = filters?.page ?? 1;
  const size = filters?.size ?? 50;
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    ...(filters?.category && { category: filters.category }),
    ...(filters?.search && { search: filters.search }),
  });

  return useQuery({
    queryKey: scoringKeys.scores(filters?.category, page),
    queryFn: async () =>
      apiFetch<{ scores: LeadScore[]; total: number; page: number; size: number }>(
        supabase,
        `app/crm/scoring/scores?${params}`,
      ),
  });
}

/**
 * Fetch detailed score breakdown for a single contact
 */
export function useScoreBreakdown(supabase: SupabaseClientLike, contactId: string) {
  return useQuery({
    queryKey: scoringKeys.breakdown(contactId),
    queryFn: async () =>
      apiFetch<{
        current_score: LeadScore;
        factors: ScoreFactor[];
        history: ScoreHistoryPoint[];
      }>(supabase, `app/crm/scoring/scores/${contactId}/breakdown`),
    enabled: !!contactId,
  });
}

/**
 * Fetch score trend history for a contact
 */
export function useScoreHistory(supabase: SupabaseClientLike, contactId: string, days = 30) {
  return useQuery({
    queryKey: scoringKeys.history(contactId),
    queryFn: async () =>
      apiFetch<{ points: ScoreHistoryPoint[] }>(
        supabase,
        `app/crm/scoring/scores/${contactId}/history?days=${days}`,
      ),
    enabled: !!contactId,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

/**
 * Manually refresh/recalculate scores for contacts
 */
export function useRefreshScores(supabase: SupabaseClientLike) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contactIds?: string[]) =>
      apiFetch<{ refreshed: number; scores: LeadScore[] }>(supabase, 'app/crm/scoring/refresh', {
        method: 'POST',
        body: JSON.stringify({ contact_ids: contactIds || 'all' }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoringKeys.all });
    },
  });
}

export default {
  useLeadScores,
  useScoreBreakdown,
  useScoreHistory,
  useRefreshScores,
};
